import { useState } from "react";
import type { Database } from "@/types/database.types";
import { KanbanColumn } from "./KanbanColumn";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];

interface KanbanViewModelProps {
  todoTasks: TaskWithDetailsRow[];
  inProgressTasks: TaskWithDetailsRow[];
  reviewTasks: TaskWithDetailsRow[];
  doneTasks: TaskWithDetailsRow[];
  onEdit: (task: TaskWithDetailsRow) => void;
  onView: (task: TaskWithDetailsRow) => void;
  onMarkComplete: (task: TaskWithDetailsRow) => void;
  onDelete: (taskId: string) => void;
  onMove: (taskId: string, toStatus: "todo" | "in_progress" | "review" | "done") => void;
  onAddTask?: (status?: "todo" | "in_progress" | "review" | "done") => void;
}

export function KanbanViewModel({
  todoTasks,
  inProgressTasks,
  reviewTasks,
  doneTasks,
  onEdit,
  onView,
  onMarkComplete,
  onDelete,
  onMove,
  onAddTask,
}: KanbanViewModelProps) {
  const [groupBy, setGroupBy] = useState<"status" | "priority">("status");

  return (
    <div className="space-y-4 w-full min-w-0">
      {/* Toggle Buttons */}
      <div className="bg-muted/50 inline-flex h-9 w-fit items-center justify-center rounded-lg p-1 gap-1">
        <button
          onClick={() => setGroupBy("status")}
          className={`inline-flex h-7 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-all ${
            groupBy === "status"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground"
          }`}
        >
          By Status
        </button>
        <button
          onClick={() => setGroupBy("priority")}
          className={`inline-flex h-7 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-all ${
            groupBy === "priority"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground"
          }`}
        >
          By Priority
        </button>
      </div>

      {/* Kanban Board */}
      <div className="w-full overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent hover:scrollbar-thumb-primary/40">
        <div className="flex gap-4 min-w-max md:min-w-0 md:w-full md:grid md:grid-cols-4">
          {groupBy === "status" ? (
            <>
              <KanbanColumn
                title="Todo"
                columnStatus="todo"
                tasks={todoTasks}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
              <KanbanColumn
                title="In Progress"
                columnStatus="in_progress"
                tasks={inProgressTasks}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
              <KanbanColumn
                title="Review"
                columnStatus="review"
                tasks={reviewTasks}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
              <KanbanColumn
                title="Done"
                columnStatus="done"
                tasks={doneTasks}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
            </>
          ) : (
            <>
              <KanbanColumn
                title="High Priority"
                columnStatus="todo"
                tasks={[...todoTasks, ...inProgressTasks, ...reviewTasks, ...doneTasks].filter(
                  (t) => t.priority === "high",
                )}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
              <KanbanColumn
                title="Medium Priority"
                columnStatus="todo"
                tasks={[...todoTasks, ...inProgressTasks, ...reviewTasks, ...doneTasks].filter(
                  (t) => t.priority === "medium" || !t.priority,
                )}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
              <KanbanColumn
                title="Low Priority"
                columnStatus="todo"
                tasks={[...todoTasks, ...inProgressTasks, ...reviewTasks, ...doneTasks].filter(
                  (t) => t.priority === "low",
                )}
                onEdit={onEdit}
                onMarkComplete={onMarkComplete}
                onDelete={onDelete}
                onView={onView}
                onMove={onMove}
                onAddTask={onAddTask}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}